import { useMemo } from "react";
import { deriveRadarState } from "../../services/retroDerivations";
import { fmtScore } from "../../services/retroFormatters";

function polar(radius, angle, cx = 180, cy = 160) {
  return {
    x: cx + Math.cos(angle) * radius,
    y: cy + Math.sin(angle) * radius,
  };
}

function ringPoints(r, count, maxR = 120) {
  const radius = (r / 10) * maxR;
  const step = (Math.PI * 2) / count;
  return Array.from({ length: count }, (_, i) => {
    const p = polar(radius, -Math.PI / 2 + i * step);
    return `${p.x},${p.y}`;
  }).join(" ");
}

function polyFromValues(values = [], maxR = 120, maxV = 10) {
  if (!values.length) return "";
  const step = (Math.PI * 2) / values.length;
  return values.map((v, i) => {
    const p = polar((Math.max(0, Math.min(maxV, v)) / maxV) * maxR, -Math.PI / 2 + i * step);
    return `${p.x},${p.y}`;
  }).join(" ");
}

export default function RadarComparisonCard({
  radar,
  period,
  setPeriod,
  seriesAId,
  setSeriesAId,
  seriesBId,
  setSeriesBId,
}) {
  const rs = useMemo(() => deriveRadarState(radar, period, seriesAId, seriesBId), [radar, period, seriesAId, seriesBId]);
  const axes = radar.axes || [];
  const tickValues = [2, 4, 6, 8, 10];
  const axisLabelRadius = 146;
  const isRoleTargetCompare = seriesBId === "__target";

  const axisMeta = useMemo(
    () =>
      axes.map((axis, idx) => {
        const cur = Number(rs?.a?.values?.[idx] || 0);
        const tgt = Number(radar?.target?.[idx] || 0);
        const diff = cur - tgt;
        return { axis, cur, tgt, diff };
      }),
    [axes, rs, radar]
  );

  return (
    <div className="rtv2-card rtv2-radar-card">
      <div className="rtv2-card-head">
        <div className="ttl">
          <h3>Performance radar <em>— vs role target</em></h3>
          <span className="sub">Tech competencies</span>
        </div>
        <div className="rtv2-radar-controls">
          <label className="rtv2-radar-sel">
            <span className="k">View</span>
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
              <option value="year">Year</option>
            </select>
          </label>
          <label className="rtv2-radar-sel">
            <span className="k">Period A</span>
            <select value={rs.a.id} onChange={(e) => setSeriesAId(e.target.value)}>
              {rs.seriesPool.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="rtv2-radar-sel">
            <span className="k">Compare to</span>
            <select value={seriesBId} onChange={(e) => setSeriesBId(e.target.value)}>
              <option value="__target">Role target</option>
              {rs.seriesPool.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
        </div>
      </div>

      <div className="rtv2-radar-body">
        <div className="rtv2-radar-stage">
          <svg viewBox="0 0 360 320" className="rtv2-radar-svg" aria-label="Performance radar">
            {tickValues.map((r) => (
              <polygon
                key={r}
                points={ringPoints(r, axes.length)}
                className={`rtv2-radar-grid${r === 10 ? " outer" : ""}`}
              />
            ))}
            {tickValues.map((r) => {
              const y = 160 - (r / 10) * 120 - 2;
              return (
                <text key={`tick-${r}`} x={176} y={y} className="rtv2-radar-tick" textAnchor="end">
                  {r}
                </text>
              );
            })}
            {axes.map((_, i) => {
              const p = polar(120, -Math.PI / 2 + i * ((Math.PI * 2) / axes.length));
              return <line key={i} x1="180" y1="160" x2={p.x} y2={p.y} className="rtv2-radar-spoke" />;
            })}
            <polygon points={polyFromValues(radar.target)} className="rtv2-radar-target" />
            <polygon points={polyFromValues(rs.a.values)} className="rtv2-radar-current" />
            {axisMeta.map((row, i) => {
              const angle = -Math.PI / 2 + i * ((Math.PI * 2) / axes.length);
              const p = polar(axisLabelRadius, angle);
              const anchor = p.x < 145 ? "end" : p.x > 215 ? "start" : "middle";
              const yNudge = p.y < 90 ? -2 : p.y > 235 ? 2 : 0;
              const valueDy = p.y < 90 ? 16 : 13;
              return (
                <text
                  key={`axis-${row.axis}`}
                  x={p.x}
                  y={p.y + yNudge}
                  className="rtv2-radar-axis"
                  textAnchor={anchor}
                >
                  <tspan className="rtv2-radar-axis-label">{row.axis}</tspan>
                  <tspan x={p.x} dy={valueDy} className={`rtv2-radar-val h${i % 8}`}>
                    {fmtScore(row.cur)}
                  </tspan>
                </text>
              );
            })}
            {axisMeta.map((row, i) => {
              const angle = -Math.PI / 2 + i * ((Math.PI * 2) / axes.length);
              const pCur = polar((Math.max(0, Math.min(10, row.cur)) / 10) * 120, angle);
              const pTgt = polar((Math.max(0, Math.min(10, row.tgt)) / 10) * 120, angle);
              return (
                <g key={`pt-${row.axis}`}>
                  <circle className="rtv2-radar-pt target" cx={pTgt.x} cy={pTgt.y} r={2.5} />
                  <circle className="rtv2-radar-pt" cx={pCur.x} cy={pCur.y} r={3.2} />
                </g>
              );
            })}
          </svg>
        </div>
        <div className="rtv2-radar-side">
          <div className="rtv2-radar-legend">
            <div className="row cur">
              <span className="sw" />
              <b>{rs.a.label}</b>
              <span className="v">{fmtScore(rs.avgA)}</span>
            </div>
            <div className="row tgt">
              <span className="sw" />
              <b>{isRoleTargetCompare ? "Role target" : rs.b.label}</b>
              <span className="v">{fmtScore(rs.avgB)}</span>
            </div>
          </div>

          <div className="rtv2-radar-summary">
            <div className="k">Closing the gap</div>
            <div className="v">
              {rs.gapPct}
              <small>% of target</small>
            </div>
            <div className={`delta ${rs.delta >= 0 ? "up" : "down"}`}>
              <b>{rs.delta >= 0 ? "+" : ""}{fmtScore(rs.delta)}</b> vs compare
            </div>
          </div>

          <div className="rtv2-radar-axes-list">
            {axisMeta.map((row) => {
              const diff = row.diff;
              const rowClass = diff < 0 ? "behind" : "ahead";
              return (
                <div key={row.axis} className={`row ${rowClass}`}>
                  <span className="nm">{row.axis}</span>
                  <span className="cur">{fmtScore(row.cur)}</span>
                  <span className="gap">{diff >= 0 ? "+" : ""}{fmtScore(diff)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
